import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";

export default function Login() {
  const { login: ctxLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [twoFA, setTwoFA] = useState(null); // { partial_token }
  const [code, setCode] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.two_factor_required) {
        setTwoFA({ partial_token: data.partial_token });
      } else {
        localStorage.setItem("ciq_token", data.token);
        // ctxLogin returns user but we already have it; trigger context update via re-call
        await ctxLogin(email, password);
        toast.success("Welcome back");
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/login-verify", {
        partial_token: twoFA.partial_token,
        code: code.trim(),
      });
      localStorage.setItem("ciq_token", data.token);
      // refresh auth context
      const me = await api.get("/auth/me");
      window.location.href = "/dashboard"; // hard nav to refresh context
      void me;
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0F1115] text-white">
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,17,21,0.55), rgba(15,17,21,0.9)), url('https://static.prod-images.emergentagent.com/jobs/81e028a2-79ed-4385-90c0-b63c7473c54d/images/515a027af52868bf4f9426a2d226ae3c6110fc857fc174de4e2e0d3d534644e8.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div>
          <div className="overline">GPI</div>
          <div className="font-serif-display text-5xl mt-2 leading-none">CarbonIQ</div>
        </div>
        <div className="max-w-md">
          <p className="font-serif-display text-3xl leading-tight">
            Carbon. Cost. Schedule.<br />
            <span className="text-gold">A single digital twin.</span>
          </p>
          <p className="text-ink-muted text-sm mt-4 leading-relaxed">
            Two-factor secured. Enterprise-ready. Built for the procurement teams that turn embodied
            carbon from a reporting burden into a deal-making lever.
          </p>
        </div>
        <div className="text-[11px] font-mono-data text-ink-dim tracking-widest">
          EN15978 · PAS 2080 · TCFD · ISSB · CSRD
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {!twoFA ? (
          <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
            <div>
              <div className="overline">Sign in</div>
              <h1 className="font-serif-display text-4xl mt-1">Welcome back.</h1>
              <p className="text-sm text-ink-muted mt-2">Access your carbon intelligence workspace.</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="overline block mb-1.5">Email</label>
                <input
                  data-testid="login-email-input"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full bg-[#1A1D24] border border-white/10 px-3 py-2.5 text-sm text-white rounded-sm focus:outline-none focus:border-gold"
                  placeholder="you@firm.com" />
              </div>
              <div>
                <label className="overline block mb-1.5">Password</label>
                <input
                  data-testid="login-password-input"
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full bg-[#1A1D24] border border-white/10 px-3 py-2.5 text-sm text-white rounded-sm focus:outline-none focus:border-gold"
                  placeholder="••••••••" />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-risk border border-risk/30 bg-risk/10 px-3 py-2">
                {error}
              </div>
            )}

            <button type="submit" data-testid="login-submit-btn" disabled={busy}
              className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50">
              {busy ? "Signing in…" : "Sign in"}
            </button>

            <div className="text-xs text-ink-muted text-center">
              Don&apos;t have an account?{" "}
              <Link to="/register" className="text-gold hover:underline" data-testid="goto-register-link">Create one</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitCode} className="w-full max-w-sm space-y-6" data-testid="login-2fa-form">
            <div className="flex items-center gap-3">
              <Shield size={22} weight="fill" className="text-gold" />
              <div>
                <div className="overline">Two-factor required</div>
                <h1 className="font-serif-display text-3xl mt-0.5">Enter your code.</h1>
              </div>
            </div>
            <p className="text-sm text-ink-muted">Open your authenticator app and enter the 6-digit code. You can also use a recovery code.</p>

            <input
              data-testid="login-2fa-code"
              value={code} onChange={(e) => setCode(e.target.value)} autoFocus required
              inputMode="numeric"
              className="w-full bg-[#1A1D24] border border-white/10 px-3 py-3 text-2xl font-mono-data text-center tracking-[0.5em] text-white rounded-sm focus:outline-none focus:border-gold"
              placeholder="123456" />

            {error && <div className="text-sm text-risk border border-risk/30 bg-risk/10 px-3 py-2">{error}</div>}

            <button type="submit" data-testid="login-2fa-submit" disabled={busy}
              className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50">
              {busy ? "Verifying…" : "Verify & continue"}
            </button>
            <button type="button" onClick={() => { setTwoFA(null); setCode(""); setError(""); }}
              className="block mx-auto text-xs text-ink-muted hover:text-white">
              ← Back to email & password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
